// Single source of truth for how each event type is presented in the UI:
// a friendly label, an icon, and a muted color tint. Used by the Logs page for
// the leading type icon (replacing raw snake_case strings), the type filter
// dropdown, the Markdown export, and delete confirmations.

import type { EventType, MedicalEvent } from "@medassist/shared";
import {
  PencilIcon,
  PillIcon,
  HeartPulseIcon,
  MoonIcon,
  ThermometerIcon,
  HeadIcon,
  StethoscopeIcon,
  SyringeIcon,
  VirusIcon,
  UsersIcon,
  UtensilsIcon,
  DropletIcon,
  BoltIcon,
  ActivityIcon,
  SmileIcon,
  AlertTriangleIcon,
  PoopIcon,
  WindIcon,
  ScalpelIcon,
  HospitalIcon,
  FlaskIcon,
  CalendarIcon,
  FileTextIcon,
  ClipboardListIcon,
  CameraIcon,
  BarbellIcon,
  CrosshairIcon,
} from "./icons.js";

interface IconProps {
  size?: number;
  className?: string;
}

export interface EventMeta {
  /** Human-readable Title Case label (e.g. "Bowel Movement"). */
  label: string;
  /** Leading icon component. */
  Icon: (props: IconProps) => JSX.Element;
  /** Muted Tailwind text-color class for the icon, tuned for light + dark. */
  color: string;
}

export const EVENT_META: Record<EventType, EventMeta> = {
  medication_taken: { label: "Medication", Icon: PillIcon, color: "text-blue-500 dark:text-blue-400" },
  as_needed_med: { label: "As-Needed Med", Icon: PillIcon, color: "text-blue-500 dark:text-blue-400" },
  patch_change: { label: "Patch Change", Icon: PillIcon, color: "text-blue-500 dark:text-blue-400" },
  vital_sign: { label: "Vital Sign", Icon: HeartPulseIcon, color: "text-rose-500 dark:text-rose-400" },
  sleep: { label: "Sleep", Icon: MoonIcon, color: "text-indigo-500 dark:text-indigo-400" },
  symptom: { label: "Symptom", Icon: ThermometerIcon, color: "text-amber-500 dark:text-amber-400" },
  headache: { label: "Headache", Icon: HeadIcon, color: "text-amber-500 dark:text-amber-400" },
  visit: { label: "Visit", Icon: StethoscopeIcon, color: "text-teal-500 dark:text-teal-400" },
  vaccination: { label: "Vaccination", Icon: SyringeIcon, color: "text-teal-500 dark:text-teal-400" },
  illness: { label: "Illness", Icon: VirusIcon, color: "text-orange-500 dark:text-orange-400" },
  family_history: { label: "Family History", Icon: UsersIcon, color: "text-slate-500 dark:text-slate-400" },
  food: { label: "Food", Icon: UtensilsIcon, color: "text-green-600 dark:text-green-400" },
  hydration: { label: "Hydration", Icon: DropletIcon, color: "text-cyan-500 dark:text-cyan-400" },
  stress_event: { label: "Stress", Icon: BoltIcon, color: "text-yellow-500 dark:text-yellow-400" },
  exercise: { label: "Exercise", Icon: ActivityIcon, color: "text-lime-600 dark:text-lime-400" },
  mood: { label: "Mood", Icon: SmileIcon, color: "text-violet-500 dark:text-violet-400" },
  allergy: { label: "Allergy", Icon: AlertTriangleIcon, color: "text-red-500 dark:text-red-400" },
  bowel_movement: { label: "Bowel Movement", Icon: PoopIcon, color: "text-stone-500 dark:text-stone-400" },
  environmental_exposure: { label: "Environmental Exposure", Icon: WindIcon, color: "text-sky-500 dark:text-sky-400" },
  surgery_or_procedure: { label: "Surgery / Procedure", Icon: ScalpelIcon, color: "text-slate-500 dark:text-slate-400" },
  hospitalization: { label: "Hospitalization", Icon: HospitalIcon, color: "text-red-500 dark:text-red-400" },
  lab_result: { label: "Lab Result", Icon: FlaskIcon, color: "text-purple-500 dark:text-purple-400" },
  cycle_event: { label: "Cycle Event", Icon: CalendarIcon, color: "text-pink-500 dark:text-pink-400" },
  report: { label: "Report", Icon: ClipboardListIcon, color: "text-gray-500 dark:text-gray-400" },
  document: { label: "Document", Icon: FileTextIcon, color: "text-gray-500 dark:text-gray-400" },
  injury: { label: "Injury", Icon: AlertTriangleIcon, color: "text-orange-500 dark:text-orange-400" },
  pt_program: { label: "PT Program", Icon: ClipboardListIcon, color: "text-emerald-600 dark:text-emerald-400" },
  pt_session: { label: "PT Session", Icon: ActivityIcon, color: "text-emerald-500 dark:text-emerald-400" },
  clinical_note: { label: "Note", Icon: PencilIcon, color: "text-slate-500 dark:text-slate-400" },
  body_composition: { label: "Body Progress", Icon: CameraIcon, color: "text-fuchsia-500 dark:text-fuchsia-400" },
  body_measurement: { label: "Body Measurement", Icon: CrosshairIcon, color: "text-fuchsia-600 dark:text-fuchsia-300" },
  gym_exercise: { label: "Gym Exercise", Icon: BarbellIcon, color: "text-cyan-600 dark:text-cyan-400" },
  gym_program: { label: "Gym Program", Icon: ClipboardListIcon, color: "text-cyan-600 dark:text-cyan-400" },
  treatment: { label: "Treatment", Icon: ActivityIcon, color: "text-teal-600 dark:text-teal-400" },
};

/** Friendly label for an event type, falling back to a Title Case of the raw
 *  string for any type not in the registry. */
export function eventTypeLabel(type: string): string {
  const meta = EVENT_META[type as EventType];
  if (meta) return meta.label;
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Returns a badge descriptor for symptom/headache timeline events
 * (SYMPTOM_FOLLOWUP_PLAN.md Phase 5):
 * - "Resolved" (green) for resolution sentinels (severity 0 + resolvedAt, D-SF-3).
 * - "Follow-up" (blue) for check-in data points created via the follow-up card (D-SF-1).
 * Returns null for first-reports and all other event types.
 */
export function symptomEventBadge(
  e: MedicalEvent
): { label: string; className: string } | null {
  if (e.eventType !== "symptom" && e.eventType !== "headache") return null;
  const p = e.payload as Record<string, unknown>;
  if (typeof p.resolvedAt === "string" || p.severity === 0) {
    return {
      label: "Resolved",
      className: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300",
    };
  }
  if (p.followUp === true) {
    return {
      label: "Follow-up",
      className: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    };
  }
  return null;
}
